import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getWithExpiry } from '../../../Domain/Helpers/LocalStorageHelper';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ENDPOINTS = {
  CASINOS:    'enabled-casinos',
  CATEGORIES: 'casino-categories',
  LIST:       'category-bonus-tags/list',
  UPDATE:     'category-bonus-tags/update',
  DELETE:     'category-bonus-tags/delete',
};

async function apiRequest(path, token, body = {}) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok || data?.error) {
    throw new Error(data?.error?.message ?? data?.error ?? data?.message ?? 'Request failed');
  }

  return data?.data;
}

// PHP returns [] instead of {} for an empty map
const toMap = (data) => (data && !Array.isArray(data) ? data : {});

function BonusTagMappingSection({ setLoggedIn }) {
  const [casinos,        setCasinos]        = useState([]);
  const [brand,          setBrand]          = useState('');
  const [categories,     setCategories]     = useState([]);
  const [saved,          setSaved]          = useState({});
  const [drafts,         setDrafts]         = useState({});
  const [loadingCasinos, setLoadingCasinos] = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [deletingKey,    setDeletingKey]    = useState(null);

  const loaded = categories.length > 0;

  const getToken = () => {
    const token = getWithExpiry('token');
    if (!token) setLoggedIn(false);
    return token;
  };

  const resetMappings = () => {
    setCategories([]);
    setSaved({});
    setDrafts({});
  };

  useEffect(() => {
    const loadCasinos = async () => {
      const token = getToken();
      if (!token) return;

      setLoadingCasinos(true);
      try {
        setCasinos(await apiRequest(ENDPOINTS.CASINOS, token) ?? []);
      } catch (err) {
        toast.error(err.message ?? 'Failed to load casinos');
      } finally {
        setLoadingCasinos(false);
      }
    };

    loadCasinos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBrandChange = (selectedBrand) => {
    setBrand(selectedBrand);
    resetMappings();
  };

  const handleLoad = async () => {
    if (!brand) return;

    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      const [categoryData, mappingData] = await Promise.all([
        apiRequest(ENDPOINTS.CATEGORIES, token, { brand }),
        apiRequest(ENDPOINTS.LIST, token, { brand }),
      ]);
      const mappings = toMap(mappingData);
      setCategories(categoryData ?? []);
      setSaved(mappings);
      setDrafts(mappings);
    } catch (err) {
      toast.error(err.message ?? 'Failed to load mappings');
      resetMappings();
    } finally {
      setLoading(false);
    }
  };

  const changedKeys = categories
    .map(({ key }) => key)
    .filter((key) => {
      const draft = (drafts[key] ?? '').trim();
      return draft !== '' && draft !== (saved[key] ?? '');
    });

  const handleSave = async () => {
    if (changedKeys.length === 0) return;

    const token = getToken();
    if (!token) return;

    const mappings = Object.fromEntries(changedKeys.map((key) => [key, drafts[key].trim()]));

    setSaving(true);
    try {
      const updated = toMap(await apiRequest(ENDPOINTS.UPDATE, token, { brand, mappings }));
      setSaved(updated);
      setDrafts(updated);
      toast.success(`Saved ${changedKeys.length} mapping(s) for ${brand}`);
    } catch (err) {
      toast.error(err.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoryKey) => {
    const token = getToken();
    if (!token) return;

    setDeletingKey(categoryKey);
    try {
      const updated = toMap(await apiRequest(ENDPOINTS.DELETE, token, { brand, categoryKey }));
      setSaved(updated);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[categoryKey];
        return next;
      });
      toast.success(`Removed bonus tag for ${categoryKey}`);
    } catch (err) {
      toast.error(err.message ?? 'Delete failed');
    } finally {
      setDeletingKey(null);
    }
  };

  const knownTags = [...new Set(Object.values(saved))];

  return (
    <div className="card-section mt-3">
      <h6 style={{ fontWeight: 700, color: '#2e2e31', marginBottom: 4 }}>Category Bonus Tags</h6>
      <p style={{ fontSize: 13, color: '#607d8b', marginBottom: 16 }}>
        Map casino categories to a bonus tag per brand. Categories without a tag get no bonus tag.
      </p>

      <div className="d-flex align-items-center gap-3 mb-3">
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 260 }}
          value={brand}
          onChange={(e) => handleBrandChange(e.target.value)}
          disabled={loadingCasinos || loading || saving}
        >
          <option value="">{loadingCasinos ? 'Loading brands…' : 'Select brand…'}</option>
          {casinos.map((casino) => (
            <option key={casino.id} value={casino.brand}>
              {casino.company_name} ({casino.brand})
            </option>
          ))}
        </select>

        <button
          className="btn btn-flexpink btn-sm"
          onClick={handleLoad}
          disabled={!brand || loading || saving}
        >
          {loading
            ? <><span className="spinner-border spinner-border-sm me-2" />Loading…</>
            : loaded ? '↻ Reload' : 'Load'
          }
        </button>
      </div>

      {loaded && (
        <>
          <datalist id="bonus-tag-options">
            {knownTags.map((tag) => <option key={tag} value={tag} />)}
          </datalist>

          <div className="table-responsive">
            <table className="table table-sm align-middle mb-3" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>Category</th>
                  <th>Bonus tag</th>
                  <th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {categories.map(({ id, key }) => {
                  const draft   = drafts[key] ?? '';
                  const isDirty = draft.trim() !== '' && draft.trim() !== (saved[key] ?? '');

                  return (
                    <tr key={id}>
                      <td>
                        {key}
                        <span style={{ color: '#9ca3af', marginLeft: 6 }}>#{id}</span>
                      </td>
                      <td>
                        <input
                          type="text"
                          list="bonus-tag-options"
                          className="form-control form-control-sm"
                          style={isDirty ? { borderColor: '#f59e0b' } : undefined}
                          placeholder="None"
                          value={draft}
                          onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                          disabled={saving}
                        />
                      </td>
                      <td className="text-end">
                        {saved[key] && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(key)}
                            disabled={saving || deletingKey !== null}
                          >
                            {deletingKey === key ? <span className="spinner-border spinner-border-sm" /> : 'Remove'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            className="btn btn-flexpink btn-sm"
            onClick={handleSave}
            disabled={saving || changedKeys.length === 0}
          >
            {saving
              ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
              : `Save changes${changedKeys.length ? ` (${changedKeys.length})` : ''}`
            }
          </button>
        </>
      )}
    </div>
  );
}

export default BonusTagMappingSection;